import {
  ChangeEvent,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react'
import classnames from 'classnames'
import PhoneInput, {
  getCountries,
  isValidPhoneNumber,
} from 'react-phone-number-input'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Box } from '../Box'
import { Icon } from '../Icon'

import { CountryOption, InputPhoneNumberProps } from './types'
import { CountrySelect } from './CountrySelect'
import { formatCountryCallingCode } from './utils'

const InputPhoneNumber = forwardRef<HTMLInputElement, InputPhoneNumberProps>(
  (
    {
      value,
      defaultCountry,
      providedId,
      label,
      errorText,
      onChange: parentOnChange,
      className,
      color = 'light',
      testId,
      variant = 'underlined',
      ...props
    },
    _ref,
  ) => {
    const { t } = useTranslation()
    const [selectedCountryOption, setSelectedCountryOption] =
      useState<CountryOption>(
        defaultCountry && getCountries().includes(defaultCountry)
          ? {
              key: defaultCountry,
              code: formatCountryCallingCode(defaultCountry),
              name: t(`countries.${defaultCountry}`),
            }
          : {
              key: 'GB',
              code: formatCountryCallingCode('GB'),
              name: t('countries.GB'),
            },
      )

    const {
      setError,
      clearErrors,
      formState: { errors },
    } = useFormContext()

    const generatedId = useId()
    const id = providedId ?? generatedId

    const hasError = Boolean(errorText)

    const helperTextId = `${id}-helper-text`

    const hasValue = useMemo(() => Boolean(value), [value])

    const onChange = useCallback(
      (evt: any | undefined) => {
        if (evt) {
          const validateNumber = isValidPhoneNumber(
            evt,
            selectedCountryOption.key,
          )

          if (!validateNumber) {
            onError(t('phone_input.error.invalid_phone'))
          } else {
            clearErrors(props.name ?? id)
          }
        }

        parentOnChange?.(evt as unknown as ChangeEvent<HTMLInputElement>)
      },
      [parentOnChange],
    )

    const onError = (message: string) => {
      setError(props.name ?? id, {
        message,
      })
    }

    return (
      <Box
        className={classnames(
          'input-group',
          {
            'input-group--error': errors,
            'input-group--has-value': hasValue || Boolean(props.placeholder),
            'input-group--dark': color === 'dark',
            'input-group--underlined': variant == 'underlined',
          },
          className,
        )}
      >
        <label htmlFor={id} className="pb-2">
          {label ? label : t('account.phone_number')}
        </label>
        <div className="input-group__control">
          <PhoneInput
            className="input-group__input group grid grid-cols-[44px,1fr] w-full [&_>_input]:pl-4 [&_>_input:focus]:outline-0"
            placeholder={`(${selectedCountryOption.code})`}
            id={id}
            data-testid={testId}
            aria-describedby={helperTextId}
            aria-invalid={hasError}
            autoComplete="off"
            value={value}
            initialValueFormat="national"
            onChange={onChange}
            defaultCountry={selectedCountryOption.key}
            countrySelectProps={{
              arrowComponent: (
                <Icon.ChevronDown className="w-1 h-1" width={4} height={4} />
              ),
            }}
            countrySelectComponent={(props) => (
              <CountrySelect
                selectedCountryOption={selectedCountryOption}
                setSelectedCountryOption={setSelectedCountryOption}
                variant={variant}
                {...props}
              />
            )}
            {...props}
          />
        </div>
        {errorText && (
          <p className="input-group__helper-text" id={helperTextId}>
            {errorText}
          </p>
        )}
      </Box>
    )
  },
)

InputPhoneNumber.displayName = 'InputPhoneNumber'

export { InputPhoneNumber }
